import { citizenShipperOrganization } from "@/seo/jsonld"
import type { OfferPriceStats } from "@/util/cost-stats"

// Documented floor for the pet Service-schema offer when the page rendered no
// priced shipments. lowPrice only — never a fabricated high/count. See
// docs/adr/0003-pet-cost-snapshot-under-ssr.md.
export const PET_SERVICE_OFFER_FLOOR = "245"

// The AggregateOffer embedded in the Service schema on the pet city/state pages
// (PetShippingCitySchemas). With price data it carries the real low/high/count
// of the shipments the page rendered, so it agrees with petCostServiceSchema;
// without it, it falls back to the documented floor. Extracted here so the two
// branches stay in one tested place and can never silently diverge.
export const buildPetServiceOffer = (
  offerPriceStats: OfferPriceStats | null,
) => {
  if (!offerPriceStats) {
    return {
      "@type": "AggregateOffer",
      lowPrice: PET_SERVICE_OFFER_FLOOR,
      priceCurrency: "USD",
    }
  }
  return {
    "@type": "AggregateOffer",
    lowPrice: String(offerPriceStats.lowPrice),
    highPrice: String(offerPriceStats.highPrice),
    offerCount: offerPriceStats.offerCount,
    priceCurrency: "USD",
  }
}

type PetCostServiceSchemaArgs = {
  // Omit `city` for the state landing page; the offer then serves the State
  // directly instead of a City contained in it.
  city?: string
  state: string
  petUrl: string
  priceStats: OfferPriceStats
}

// Mirrors boatCostServiceSchema (src/seo/boat-rich-snippets.ts): an
// AggregateOffer modelling the page as a marketplace listing, with CitizenShipper
// as the seller (never the provider). priceStats is required by design — the
// price range comes from the prices of the shipments rendered on the page
// (computeOfferPriceStats over parsed pet `price` strings), never a hardcoded
// figure. Callers skip emitting this schema when no price data exists.
export const petCostServiceSchema = ({
  city,
  state,
  petUrl,
  priceStats,
}: PetCostServiceSchemaArgs) => {
  const location = city ? `${city}, ${state}` : state
  const areaServed = city
    ? {
        "@type": "City",
        name: city,
        containedInPlace: { "@type": "State", name: state },
      }
    : { "@type": "State", name: state }

  return {
    "@context": "https://schema.org",
    "@type": "AggregateOffer",
    name: `${location} Pet Shipping`,
    description: `Marketplace listing aggregating pet transportation offers from independent transporters serving ${location} via the CitizenShipper marketplace.`,
    url: petUrl,
    priceCurrency: "USD",
    lowPrice: String(priceStats.lowPrice),
    highPrice: String(priceStats.highPrice),
    offerCount: priceStats.offerCount,
    seller: citizenShipperOrganization,
    itemOffered: {
      "@type": "Service",
      serviceType: "Pet Transportation",
      name: `${location} Pet Transportation`,
      areaServed,
    },
  }
}
